import {createSelector} from '@reduxjs/toolkit'

import {ItemDiscountCategory, MerchantDiscountCategory, PolicyDiscountCategory} from '@web/core/constants'

import {
    RootState,
    ItemBoundCoupons,
    DeliveryDiscountCoupon,
    SubscriptionDiscountCoupon,
    AppliedDiscount,
    MerchantBoundCoupons,
} from './interfaces'

export const couponSelector = (state: RootState) => state.coupon

type CouponInfo = {
    immediateCoupons: ItemBoundCoupons['immediateDiscount']
    productCoupons: ItemBoundCoupons['productDiscount']
    duplicateCoupons: ItemBoundCoupons['duplicateDiscount']
    merchantCoupons: MerchantBoundCoupons['merchantDiscount']
    deliveryFeeCoupons: DeliveryDiscountCoupon[]
    subscribeCoupons: SubscriptionDiscountCoupon[]
}
/**
 * flat하게 정렬한 쿠폰 정보 selector
 **/
export const couponsInfoSelector = createSelector(couponSelector, (state): CouponInfo => {
    if (!state) {
        return {
            immediateCoupons: [],
            productCoupons: [],
            duplicateCoupons: [],
            merchantCoupons: [],
            deliveryFeeCoupons: [],
            subscribeCoupons: [],
        }
    }

    const {coupons} = state

    const itemCoupons = Object.values(coupons.items).reduce<Required<ItemBoundCoupons>>(
        (acc, item) => {
            acc.immediateDiscount = acc.immediateDiscount.concat(item.immediateDiscount ?? [])
            acc.productDiscount = acc.productDiscount.concat(item.productDiscount ?? [])
            acc.duplicateDiscount = acc.duplicateDiscount.concat(item.duplicateDiscount ?? [])
            return acc
        },
        {immediateDiscount: [], productDiscount: [], duplicateDiscount: []},
    )

    const merchantCoupons = Object.values(coupons.merchants).reduce<Required<MerchantBoundCoupons>>(
        (acc, merchant) => {
            if (merchant.merchantDiscount) {
                acc.merchantDiscount = acc.merchantDiscount.concat(merchant.merchantDiscount)
            }
            return acc
        },
        {merchantDiscount: []},
    )

    const deilveryFeeCoupons = Object.values(coupons.deliveryGroups).reduce<DeliveryDiscountCoupon[]>(
        (acc, deliveryGroup) => {
            if (deliveryGroup.deliveryDiscount) {
                acc.push(deliveryGroup.deliveryDiscount)
            }
            return acc
        },
        [],
    )

    const subscriptionCoupons = Object.values(coupons.subscriptions).reduce<SubscriptionDiscountCoupon[]>(
        (acc, subscription) => {
            if (subscription.subscrptionDiscount) {
                acc.push(subscription.subscrptionDiscount)
            }
            return acc
        },
        [],
    )

    return {
        immediateCoupons: itemCoupons.immediateDiscount,
        productCoupons: itemCoupons.productDiscount,
        duplicateCoupons: itemCoupons.duplicateDiscount,
        merchantCoupons: merchantCoupons.merchantDiscount,
        deliveryFeeCoupons: deilveryFeeCoupons,
        subscribeCoupons: subscriptionCoupons,
    }
})

/**
 * 아이템에 적용된 할인금액 selector
 * 각 아이템에 적용된 할인을 유형별로 분류하여 리턴한다
 */
type ItemDiscountSummary = Record<string, {item: number; merchant: number; multiPurchase: number; total: number}>
export const itemDiscountAmountSelector = createSelector(couponSelector, (state): ItemDiscountSummary => {
    if (!state) {
        return {}
    }

    const itemDiscountMap = Object.entries<AppliedDiscount[]>(
        state.discountSummary.applied.items,
    ).reduce<ItemDiscountSummary>((acc, [itemKey, discounts]) => {
        acc[itemKey] = discounts.reduce<ItemDiscountSummary[0]>(
            (map, discount) => {
                switch (discount.category) {
                    case MerchantDiscountCategory.MERCHANT:
                        map.merchant += discount.discountAmount
                        break
                    case PolicyDiscountCategory.MULTI_PURCHASE:
                        map.multiPurchase += discount.discountAmount
                        break
                    case ItemDiscountCategory.PRODUCT:
                    case ItemDiscountCategory.IMMEDIATE:
                    default:
                        map.item += discount.discountAmount
                        break
                }

                map.total += discount.discountAmount
                return map
            },
            {item: 0, merchant: 0, multiPurchase: 0, total: 0},
        )

        return acc
    }, {})

    return itemDiscountMap
})

export const discountSummaryByItemIdSelector = createSelector(
    [itemDiscountAmountSelector, (_, discountItemId: string) => discountItemId],
    (itemDiscountMap, discountItemId) => {
        const discountAmount = itemDiscountMap[discountItemId]?.item ?? 0

        return {
            discountAmount,
            hasDiscount: discountAmount > 0,
        }
    },
)
